import { handleMutationException } from 'utils/error'
import { useHistory } from 'react-router'
import { getQueries } from 'graphql/util'
import { useMutation } from '@apollo/client'
import { GET_PROJECTS, GET_PROJECT } from 'graphql/queries/project/projectQueries'
import { isObject } from 'util'
import { unfreeze } from 'utils'
import { MAX_RETY_ON_QUERY_ERROR } from 'utils/constants'
import { GET_MODELLING_PROJECT } from 'graphql/queries/modellingProjectQueries'
import { traverseAndApply } from 'pages/Modelling/ModellingEditPage/SharedComponents/Tree/TreeMap/util'

import { useSetProject } from './projects'
import { getUserId } from './user'
import { useEvents } from './events'

/* **********************************************
 *  LEGACY METHODS - TO AVOID COMPLETE REWRITE  *
 *  preferred: useMutation directly             *
 * ******************************************** */

// Reduce multiple cruds to previous namespace-format e.g. saveRemote[NS]()
export const reduceCruds = (cruds) => {
  if (!cruds) return null
  const crud = { addRemote: {}, saveRemote: {}, deleteRemote: {} }

  cruds.forEach((c) => {
    Object.keys(crud).forEach((o) => {
      crud[o][c.namespace] = c[o][c.namespace]
      crud[o].loading = crud[o].loading || c[o].loading
    })
  })

  return crud
}

export const useCrudMutation = (namespace, filters = {}) => {
  const addRemote = useAddRemote({ namespace, updateFilter: filters.saveUpdateFilter })
  const saveRemote = useSaveRemote({ namespace, updateFilter: filters.saveUpdateFilter })
  const deleteRemote = useDeleteRemote({ namespace, updateFilter: filters.deleteUpdateFilter })

  return { addRemote, saveRemote, deleteRemote, namespace }
}

export const useDeleteRemote = ({ namespace, updateFilter = defaultDeleteFilter }) => {
  const { deleteRemote, deleteOp } = getQueries(namespace)
  const userId = getUserId()
  const params = getParams(namespace, deleteOp, 'DELETE', updateFilter)
  const publishEvent = useEvents()

  const [deleteRemoteOp, { loading }] = useMutation(deleteRemote, params)

  // Edge case: Delete yourself from project
  const resetProjectIndex = useSetProject()
  const { push } = useHistory()
  const safeDelete = (params) => {
    if (namespace === 'projectMemberships' && params.variables.userId === userId) {
      // Override the update function to avoid an access-denied error
      params.update = (store) => {
        resetProjectIndex()
        updateProjectsQuery(params.variables.projectId, store)
        push('/overview')
      }
    }
    return deleteRemoteOp(params)
  }

  return {
    [namespace]: safeOp(safeDelete, report(publishEvent, namespace, 'DELETE')),
    loading,
  }
}

export const useSaveRemote = ({ namespace, updateFilter = defaultFilter }) => {
  const { saveRemote, saveOp } = getQueries(namespace)
  const params = getParams(namespace, saveOp, 'SAVE', updateFilter)
  const publishEvent = useEvents()

  const [saveRemoteOp, { loading }] = useMutation(saveRemote, params)

  return {
    [namespace]: safeOp(saveRemoteOp, report(publishEvent, namespace, 'SAVE')),
    loading,
  }
}

export const useAddRemote = ({ namespace, updateFilter = defaultFilter }) => {
  const { addRemote, addOp } = getQueries(namespace)
  const publishEvent = useEvents()

  const params = getParams(namespace, addOp, 'ADD', updateFilter)
  const [addRemoteOp, { loading }] = useMutation(addRemote, params)

  return {
    [namespace]: safeOp(addRemoteOp, report(publishEvent, namespace, 'ADD')),
    loading,
  }
}

/* ******************************************** *
 *                 HELPER FUNCTIONS             *
 * ******************************************** */

const report = (publishEvent, namespace, opType) => (response) => {
  const category = namespace.toLocaleUpperCase()
  const type = `${opType.toLocaleUpperCase()} ${category}`
  publishEvent(category, type)
  return response
}

// Function for safe add/delete/edit operations.
// Catches optimistic locking exceptions (e.g. due to fast editing of same project-entities) and retries
export const safeOp = (mutation, callback) => {
  const retryOp = (vars, retryNTimes = 0) => {
    try {
      return mutation(vars).then(callback)
    } catch (e) {
      const { type, message } = e
      switch (type) {
        case 'OptimisticLockException': // failure due to simultanuous editing
        case 'ObjectOptimisticLockingFailureException':
          if (retryNTimes > MAX_RETY_ON_QUERY_ERROR) handleMutationException(e)
          console.log('Simultanuous editing, trying again.', message, retryNTimes, vars)
          return retryOp(vars, retryNTimes + 1)
        default:
          handleMutationException(e)
          return Promise.reject(e)
      }
    }
  }

  return retryOp
}

// Default filter for an entity
const defaultFilter = (entity) => (i) => i.id === entity.id
const defaultDeleteFilter = (eId) => (i) => i.id === eId

// Creates the update and on error function for mutations
const getParams = (namespace, operation, operationType, updateFilter, stagingId = null) => {
  return {
    onError: (err) => handleMutationException(err),
    update: (store, { data }) => {
      const updatedEntity = data[operation]
      let updatedProject

      if (updatedEntity.__typename === 'ModellingProject') {
        store.writeQuery({
          query: GET_MODELLING_PROJECT,
          variables: { modellingProjectId: updatedEntity.id },
          data: { modellingProject: updatedEntity },
        })
      } else {
        // Fully simulated project returned (e.g. because update influenced other entities)
        if (updatedEntity.__typename === 'BaseProject') updatedProject = updatedEntity
        // Special case: Deletion of full project
        else if (operationType === 'DELETE' && namespace === 'projects') {
          updateProjectsQuery(updatedEntity, store)
          return
        }
        // Default case: Only project-entity returned instead of full project
        else {
          const project = unfreeze(getProject(store))

          updatedProject = wrapProject(
            project,
            namespace,
            operationType,
            updatedEntity,
            updateFilter,
            stagingId,
          )
        }

        updateProjectQuery(updatedProject, store)
        updateProjectsQuery(updatedProject, store)
      }
    },
  }
}

export const getProject = (store) => {
  const userId = getUserId()
  const { projects, projectIndex } = store.readQuery({ query: GET_PROJECTS, variables: { userId } })
  const projectId = projects[projectIndex].id
  const { project } = store.readQuery({ query: GET_PROJECT, variables: { projectId } })

  return project
}

export const updateProjectQueries = (operationName) => (store, { data }) => {
  if (data[operationName].__typename === 'ModellingProject') {
    store.writeQuery({
      query: GET_MODELLING_PROJECT,
      variables: { modellingProjectId: data[operationName].id },
      data: { modellingProject: data[operationName] },
    })
  } else {
    updateProjectQuery(data[operationName], store)
    updateProjectsQuery(data[operationName], store)
  }
}

export const updateProjectQuery = (project, store) => {
  store.writeQuery({ query: GET_PROJECT, variables: { projectId: project.id }, data: { project } })
}

// Function to update the PROJECTS query in the store
export const updateProjectsQuery = (project, store) => {
  const userId = getUserId()
  let { projects } = store.readQuery({ query: GET_PROJECTS, variables: { userId } })
  projects = unfreeze(projects)

  // DELETE (SPECIAL CASE): Full Project deletion -> Only ID returned
  if (!isObject(project)) projects = projects.filter((p) => !defaultDeleteFilter(project)(p))
  else {
    const idx = projects.findIndex(defaultFilter(project))
    // SAVE: Existing project, override with update
    if (idx !== -1) projects[idx] = project
    // ADD: Newly added project (e.g. new membership, or complete project)
    else projects.unshift(project)
  }

  store.writeQuery({ query: GET_PROJECTS, variables: { userId }, data: { projects } })
}

/**
 * Helper function to update any entity with the operation and return the updated project
 * @param {The project data from the store before update} project
 * @param {Namespace of the updated entity} namespace
 * @param {Operation applied to the updated entity} operation
 * @param {The updated entity} entity
 * @param {Update filter (e.g. if not based on ID but userId)} updateFilter
 */
const wrapProject = (
  project,
  namespace,
  operation,
  entity,
  updateFilter = defaultFilter,
  stagingId,
) => {
  const isEvent = [
    'investments',
    'buyOuts',
    'manualShares',
    'esopGrants',
    'esops',
    'manualShares',
  ].includes(namespace)

  if (isEvent) {
    switch (operation) {
      case 'ADD':
        project.capTables.forEach((c) => {
          c.untriggeredEvents.push(entity)
        })
        return project
      case 'SAVE':
        project.capTables.forEach((c) => {
          c.participations.forEach((p) => {
            p.entries = p.entries.map((e) =>
              e.event.id === entity.id ? { ...e, event: entity } : e,
            )
          })
          c.untriggeredEvents = c.untriggeredEvents.map((e) => (e.id === entity.id ? entity : e))
        })
        return project
      case 'DELETE':
        return filterProjectEntries(project, (e) => e.event.id !== entity.id)
      default:
        return project
    }
  }

  let obj = project[namespace]

  switch (operation) {
    case 'ADD':
      if (Array.isArray(obj)) {
        const idx = obj.findIndex((i) => i.id === stagingId || updateFilter(entity)(i))
        if (idx !== -1) obj[idx] = entity
        // Optimistic response already -> replace instead of add
        else obj.unshift(entity)
      } else obj = entity
      break
    case 'SAVE':
      // update entities-list
      if (Array.isArray(obj)) {
        const idx = obj.findIndex(updateFilter(entity))
        obj[idx] = { ...obj[idx], ...entity }
        // update single entity
      } else obj = { ...obj, ...entity }
      break
    case 'DELETE':
      // delete from entities-list/single entity
      if (Array.isArray(obj)) obj = obj.filter((i) => !updateFilter(entity)(i))
      else obj = null
      break
    default:
      obj = null
  }

  project[namespace] = obj

  return project
}

// Optimistic Response Helper functions

export const filterProjectEntries = (project, entryFilterFunc) => {
  return {
    ...project,
    events: project.events.filter((e) => entryFilterFunc({ event: e })),
    entries: project.entries.filter(entryFilterFunc),
    capTables: project.capTables.map((c) => filterCapTableEntries(c, entryFilterFunc)),
  }
}

export const deleteProjectEvents = (project, deletedEvents) => {
  let updatedProject
  if (project.__typename === 'ModellingProject') {
    updatedProject = updateModellingProjectCapTables(project, deletedEvents, deleteCapTableEvents)
  } else {
    updatedProject = filterProjectEntries(
      project,
      (e) => !deletedEvents.some((t) => t.id === e.event.id),
    )
  }

  return updateProjectEventsAndEntries(updatedProject, [], [], deletedEvents)
}

export const addProjectEvents = (project, newEvents) => {
  let updatedProject
  if (project.__typename === 'ModellingProject') {
    updatedProject = updateModellingProjectCapTables(project, newEvents, addCapTableEvents)
  } else {
    updatedProject = {
      ...project,
      capTables: project.capTables.map((c) => addCapTableEvents(c, newEvents)),
    }
  }

  return updateProjectEventsAndEntries(updatedProject, newEvents)
}

export const saveProjectEvents = (project, updatedEvents) => {
  let updatedProject
  if (project.__typename === 'ModellingProject') {
    updatedProject = updateModellingProjectCapTables(project, updatedEvents, saveCapTableEvents)
  } else {
    updatedProject = {
      ...project,
      capTables: project.capTables.map((c) => saveCapTableEvents(c, updatedEvents)),
    }
  }

  return updateProjectEventsAndEntries(updatedProject, [], updatedEvents)
}

export const updateProjectEvents = (project, newEvents, updatedEvents, deletedEvents) => {
  let updatedProject
  if (project.__typename === 'ModellingProject') {
    updatedProject = updateModellingProjectCapTables(
      project,
      [...newEvents, ...updatedEvents, ...deletedEvents],
      (capTable) => updateCapTableEvents(capTable, newEvents, updatedEvents, deletedEvents),
    )
  } else {
    updatedProject = {
      ...project,
      capTables: project.capTables.map((c) =>
        updateCapTableEvents(c, newEvents, updatedEvents, deletedEvents),
      ),
    }
  }

  return updateProjectEventsAndEntries(updatedProject, newEvents, updatedEvents, deletedEvents)
}

const updateProjectEventsAndEntries = (
  project,
  newEvents = [],
  updatedEvents = [],
  deletedEvents = [],
) => {
  const newEsopStakeholders = newEvents.filter((e) => e.type === 'ESOP_CREATION').map((e) => e.esop)
  return {
    ...project,
    stakeholders:
      newEsopStakeholders.length > 0
        ? [...project.stakeholders, ...newEsopStakeholders]
        : project.stakeholders,
    entries: project.entries
      .map((e) => ({
        ...e,
        event: updatedEvents.find((t) => t.id === e.event.id) || e.event,
      }))
      .filter((e) => !deletedEvents.some((t) => t.id === e.event.id)),
    events: [
      ...project.events.map((e) => updatedEvents.find((t) => t.id === e.id) || e),
      ...newEvents,
    ].filter((e) => !deletedEvents.some((t) => t.id === e.id)),
  }
}

export const applyCapTablesProject = (project, round, applyCapTableFunc) => {
  let updatedProject
  if (project.__typename === 'ModellingProject') {
    updatedProject = updateModellingProjectCapTables(project, [{ round }], applyCapTableFunc)
  } else {
    updatedProject = {
      ...project,
      capTables: project.capTables.map((c) =>
        c.termsheet.round.id === round.id ? applyCapTableFunc(c) : c,
      ),
    }
  }

  return updatedProject
}

const updateModellingProjectCapTables = (project, events, applyCapTableFunc) => {
  if (events.length === 0) return project
  const { round } = events[0]
  return {
    ...project,
    calculationTree: traverseAndApply(
      project.calculationTree,
      round.id,
      (n) => ({ ...n, result: applyCapTableFunc(n.result, events) }),
      (n) => n.result.termsheet.round.id,
    ),
  }
}

const filterCapTableEntries = (capTable, entryFilterFunc) => {
  return {
    ...capTable,
    participations: capTable.participations
      .map((p) => ({
        ...p,
        entries: p.entries.filter(entryFilterFunc),
      }))
      .filter((p) => p.entries.filter((e) => ['ADD', 'SUB'].includes(e.operation)).length > 0),
    untriggeredEvents: capTable.untriggeredEvents.filter((e) => entryFilterFunc({ event: e })),
  }
}

const deleteCapTableEvents = (capTable, deletedEvents) => {
  const entryFilterFunc = (e) => !deletedEvents.some((t) => t.id === e.event.id)
  return filterCapTableEntries(capTable, entryFilterFunc)
}

const addCapTableEvents = (capTable, newEvents) => {
  return {
    ...capTable,
    untriggeredEvents: [...capTable.untriggeredEvents, ...newEvents],
  }
}

const saveCapTableEvents = (capTable, updatedEvents) => {
  return {
    ...capTable,
    participations: capTable.participations.map((p) => ({
      ...p,
      entries: p.entries.map((e) => ({
        ...e,
        event: updatedEvents.find((t) => t.id === e.event.id) || e.event,
      })),
    })),
  }
}

const updateCapTableEvents = (capTable, newEvents, updatedEvents, deletedEvents) => {
  let updatedCapTable = deleteCapTableEvents(capTable, deletedEvents)
  updatedCapTable = saveCapTableEvents(updatedCapTable, updatedEvents)
  return addCapTableEvents(updatedCapTable, newEvents)
}
